import { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '@redux-actions/';
import { getInsuranceBenefits, getInsuranceCode, getLabels } from '@utils/utils';
import useWindowSize from '@hooks/useWindowSize';
import {isMobile} from '@utils/utils';
import CartLoader from '@components/CartLoader';
import Redirect from '@components/Redirect';

import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import TypPage from './pages/TypPage';

import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { Helmet } from 'react-helmet';

declare global {
    interface Window {
        globalMethods: any;
        BindIDme: any;
        paypal_disabled: boolean;
        products: any;
        tealium_data2track: any;
        affirm: any;
        utag: any;
        forterWebId: any;
        klarnaAsyncCallback: any;
        KlarnaOnsiteService: any;
        monetateQ: any;
    }
}

export default ({data}) => {
    const dispatch = useDispatch();
    
    const [configLoaded, setConfigLoaded] = useState(false);
    const [isKeyboardUser, setIsKeyboardUser]= useState(false);

    const openModal = useSelector(state => state.cart.openModal);
    const config = useSelector(state => state.cart.config);
    const isLoading = useSelector(state => state.cart.isLoading);
    
    /*
        globalMethods -  object that contains all the function called from the functions defined in
        'callFromComponentToGl' object in config object.
    */
    const globalMethods = {
        backFromLogin: () => {
            if(window.location.pathname !== data.pagesUrl.typ) {
                dispatch(actions.populateCart(data.mockRequests, data.hostname));
            }
        },
        backFromCreateAccount: () => {
            if(window.location.pathname !== data.pagesUrl.typ) {
                dispatch(actions.populateCart(data.mockRequests, data.hostname));
            }
        },
        onInsuranceClosing: (value) => {
            if(typeof config != 'undefined' && config != null && config.cartLensUpsell.enabled === true){
                dispatch(actions.endLoading());
            }
            dispatch(actions.setInsurance(undefined));
            dispatch(actions.setInsurance(value));
        },
        onRecalculateInsuranceEnd: (value) => {
            if(config.cartLensUpsell.enabled === true){
                dispatch(actions.endLoading());
            }           
            dispatch(actions.setInsurance(undefined));
            dispatch(actions.setInsurance(value));

            if(value !== true) {
                window?.tealium_data2track?.push({
                    'tealium_event': 'Insurance-Tentative',
                    'id': 'Event',
                    'Order_InsuranceCode': 'Unknown',
                    'Events_Error': '1',
                    'Error_Source': 'User',
                    'Error_Code': 'InsurancePanel',
                    'Error_Details': 'Insurance error',
                    'Error_Message': 'Insurance error',
                    'Events_InsuranceTentative': '1'
                })
            }
        },
        endCartLoading: () => {
            dispatch(actions.endLoading());
        }
    }

    window.globalMethods = globalMethods;

    // for accessibility
	const setKeyboardUser = e => {
		if (e.code === 'Tab') {
			setIsKeyboardUser(true);
		}
	}

	const unsetKeyboardUser = () => {
       setIsKeyboardUser(false);
	}

    /* At first render, save the config, get labels, and calls getCart */
    useEffect(() => {
        dispatch(actions.saveConfig(data));
        getLabels(data.langUrl);
        if(window.location.pathname !== data.pagesUrl.typ) {
            dispatch(actions.populateCart(data.mockRequests, data.hostname, data));
        }

        //attach events for 'Tab' navigation
        window.addEventListener('keydown', setKeyboardUser);
		window.addEventListener('mousedown', unsetKeyboardUser);
    }, []);

    const size = useWindowSize();

    //every time the size of the window changes, control if it is a mobile or desktop view
    useEffect(() => {
        const isMobileValue = isMobile.any() || size.width < 1200
        dispatch(actions.setIsMobile(isMobileValue));
    }, [size])

    //when the config is loaded, set the corresponding state variable
    useEffect(() => {
        if(!!config && configLoaded === false)  {
            setConfigLoaded(true);
        }
    }, [config]);
    
    return (
        <div id="glasses-headless-checkout" className={`glasses-headless-checkout ${isKeyboardUser === true ? 'isKeyboardUser' : ''} ${openModal === true || isLoading === true ? 'openModal' : ''}`}>
            {!!config &&
                <PayPalScriptProvider options={
                    {
                        "client-id": config.paypal.clientKey,
                        currency: "USD",
                        intent: "order",
                        components: "messages,buttons",
                    }
                }>
                    <Suspense fallback={<CartLoader />}>
                        <Router>                      
                            <Routes>
                                <Route path={config.pagesUrl.cart} element={<CartPage />} />
                                <Route path={config.pagesUrl.shipping} element={<CheckoutPage />} />
                                <Route path={config.pagesUrl.payment} element={<CheckoutPage />} />
                                <Route path={config.pagesUrl.typ} element={<TypPage />} />
                                <Route path='*' element={<Redirect to={config.pagesUrl.cart} />} />
                            </Routes>
                        </Router>
                    </Suspense>
                </PayPalScriptProvider>
            }
            {!config &&
                <CartLoader />
            }
            {/* Asynchronously add in the Head of the HTML page the affirm script */}
            {configLoaded &&
                <Helmet>
                    <script type="text/javascript">
                       {`
                            (function(l,g,m,e,a,f,b){var d,c=l[m]||{},h=document.createElement(f),n=document.getElementsByTagName(f)[0],k=function(a,b,c){return function(){a[b]._.push([c,arguments])}};c[e]=k(c,e,"set");d=c[e];c[a]={};c[a]._=[];d._=[];c[a][b]=k(c,a,b);a=0;for(b="set add save post open empty reset on off trigger ready setProduct".split(" ");a<b.length;a++)d[b[a]]=k(c,e,b[a]);a=0;for(b=["get","token","url","items"];a<b.length;a++)d[b[a]]=function(){};h.async=!0;h.src=g[f];n.parentNode.insertBefore(h,n);delete g[f];d(g);l[m]=c})(window,{
                                public_api_key: "${config.affirm.apiKey}",
                                script: "${config.affirm.script}"
                            },"affirm","checkout","ui","script","ready");
                        `}
                    </script>
                    <script async src={config.klarna.klarnaSrc} data-client-id={config.klarna.klarnaDataClientId}></script>

                </Helmet>
            }
        </div>
    );
};